import React, { useState } from "react";
import moment from "moment";
import { LuEye, LuLayers } from "react-icons/lu";

import Badge from "../ui/Badge";
import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";
import { activityLogModuleLabel } from "../../static/activityLog";
import { ActivityLogProps } from "../../types/activityLog";
import ActivityLogDetailModal from "./ActivityLogDetailModal";

type Props = {
  logs: ActivityLogProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
};

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const actionBadge = (action: string) =>
  action === "update" ? (
    <Badge
      variant="none"
      className="bg-blue-100 text-blue-800 hover:bg-blue-100"
    >
      અપડેટ
    </Badge>
  ) : (
    <Badge variant="none" className="bg-red-100 text-red-800 hover:bg-red-100">
      ડિલીટ
    </Badge>
  );

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const ActivityLogList = ({ logs, pageNumber, pageSize, isLoading }: Props) => {
  const [selectedLog, setSelectedLog] = useState<ActivityLogProps | null>(null);

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                  <TableHead className={tableHeadCellClass}>યુઝર</TableHead>
                  <TableHead className={tableHeadCellClass}>એકશન</TableHead>
                  <TableHead className={tableHeadCellClass}>મોડ્યુલ</TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    એકશન્સ બટન
                  </TableHead>
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={6} />
              ) : logs.length === 0 ? (
                <TableEmptyRow
                  colSpan={6}
                  label="એક્ટિવિટી લોગ ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {logs.map((log, index) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>{log.user?.name || "-"}</TableCell>

                      <TableCell>{actionBadge(log.action)}</TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <LuLayers className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div>
                            <div className="font-medium">
                              {activityLogModuleLabel(log.model_name)}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {log.model_id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="text-sm text-gray-500">
                        {formatDate(log.created_at)}
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="iconSm"
                            title="વિગતો જુઓ"
                            onClick={() => setSelectedLog(log)}
                          >
                            <LuEye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : logs.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="એક્ટિવિટી લોગ ની વિગતો મળી નથી" />
          </div>
        ) : (
          logs.map((log, index) => (
            <div key={log.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                <div className="flex min-w-0 items-center gap-2">
                  <LuLayers className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <div className="font-medium break-words">
                      {activityLogModuleLabel(log.model_name)}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {log.model_id}
                    </div>
                  </div>
                </div>

                {actionBadge(log.action)}
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>યુઝર</span>
                  <span className="min-w-0 text-right break-words">
                    {log.user?.name || "-"}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(log.created_at)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 border-t p-4">
                <Button
                  variant="outline"
                  title="વિગતો જુઓ"
                  onClick={() => setSelectedLog(log)}
                >
                  <LuEye className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedLog && (
        <ActivityLogDetailModal
          log={selectedLog}
          moduleLabel={activityLogModuleLabel(selectedLog.model_name)}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </>
  );
};
export default ActivityLogList;
